import { ChangeEvent, useLayoutEffect, useRef, useState } from 'react';
import { ArrowRightCircleIcon } from '@heroicons/react/20/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

import {
    trackEngagement,
    trackFieldInteraction,
    TrackingFieldStatus,
    uniqueIdTransfer,
    useFieldValidation,
} from '@/apiCalls/uniqueIDTransferHelper';
import { EJourneyEvent, emitJourneyEvent } from '@/journeyEvents';
import { defaultRequiredMessage } from '@/utils/fieldMessages';
import { trimClassname } from '@/utils/styleHelper';

import { EEventType } from '../eventType';
import { FieldLabel } from '../FieldLabel';

export interface IInput {
    title?: string;
    placeholder: string;
    id: string;
    onChange?: (value: string) => void;
    onBlur?: () => void;
    showErrors?: boolean;
    disabled?: boolean;
    value?: string;
    prefix?: string;
    /** Unit shown immediately after the value, e.g. a percent sign. */
    suffix?: string;
    /**
     * Unit pinned to the field's right edge, e.g. "years".
     *
     * Deliberately different from `suffix`, which chases the number: a symbol
     * like "%" belongs to the digits, but a word naming what the number counts
     * reads as part of the field. Pinning it also lines the units of two
     * side-by-side fields up on one edge, which is the point of using it.
     */
    unit?: string;
    /**
     * Accessible name for a field whose question is carried by a shared label
     * above it, so the input itself has no visible title of its own.
     */
    ariaLabel?: string;
    /** Message shown under the field, e.g. what needs correcting. */
    message?: string;
    /** How the message reads. 'error' is something to fix, 'hint' is guidance. */
    tone?: 'error' | 'hint';
    showNumpad?: boolean;
    type?: string;
    eventType?: EEventType;
    tooltipContent?: string;
    /** Field wrappers are required by default; optional fields opt out. */
    required?: boolean;
    onArrow?: () => void;
    height?: 'normal' | 'large';
    disclaimer?: boolean;
    disclaimerClick?: () => void;
    pulseErrors?: boolean;
}

export function Input({
    title,
    placeholder,
    id,
    value,
    showErrors,
    disabled = false,
    onChange,
    onBlur,
    prefix,
    suffix,
    unit,
    ariaLabel,
    message,
    tone = 'error',
    showNumpad,
    type = 'text',
    eventType = EEventType.INPUT,
    tooltipContent,
    required = true,
    height = 'normal',
    onArrow,
    disclaimer,
    disclaimerClick,
    pulseErrors = false,
}: IInput): JSX.Element {
    // Field tracking state. None of this holds what the applicant typed — only
    // whether the field had a value, how many times it was revised, and how
    // long it was focused.
    // Records the moment this field is shown an error.
    useFieldValidation(id, showErrors);

    // showErrors already means this field is blocking the form, so it is the
    // whole condition. Checking for an empty value as well missed fields that
    // are flagged while holding a placeholder value, such as a price of 0.
    // A hint is guidance and is always shown. An error describes a fault, so
    // it appears only while the field is flagged, whether it is the default
    // wording or one the field supplied itself.
    const resolvedMessage =
        tone === 'hint'
            ? message
            : showErrors
            ? message ??
              defaultRequiredMessage(
                  typeof title === 'string' ? title : undefined
              )
            : undefined;

    const messageId = `${id}-message`;
    const helpId = `${id}-help`;

    const inputRef = useRef<HTMLInputElement>(null);
    const [suffixLeft, setSuffixLeft] = useState<number | null>(null);

    // The value is left aligned, so a unit pinned to the right edge would sit
    // far from the number it belongs to. Measure the rendered text and put the
    // unit immediately after it instead.
    useLayoutEffect(() => {
        if (!suffix) return;
        const el = inputRef.current;
        if (!el || !el.value) {
            setSuffixLeft(null);
            return;
        }
        const styles = window.getComputedStyle(el);
        const context = document.createElement('canvas').getContext('2d');
        if (!context) return;
        context.font = styles.font;
        const textWidth = context.measureText(el.value).width;
        const limit = el.clientWidth - parseFloat(styles.paddingRight);
        setSuffixLeft(
            Math.min(parseFloat(styles.paddingLeft) + textWidth + 2, limit)
        );
    }, [suffix, value]);

    const focusedAt = useRef<number | null>(null);
    const hadValueOnFocus = useRef(false);
    const editCount = useRef(0);

    function handleChange(event: ChangeEvent<HTMLInputElement>): void {
        const { value } = event.target;
        let protectedValue = value;
        if (prefix) {
            protectedValue = value.replace(prefix, '');
        }

        editCount.current += 1;
        onChange && onChange(protectedValue);
    }

    function handleFocus(): void {
        focusedAt.current =
            typeof performance !== 'undefined' ? performance.now() : Date.now();
        hadValueOnFocus.current = Boolean(value);
        editCount.current = 0;

        // Focusing a field is the first thing an applicant does that counts as
        // actually using the tool, as opposed to the widget merely rendering.
        trackEngagement(`field:${id}`);

        // Separate emitter at the same moment, deliberately not folded into
        // trackEngagement: that is our own telemetry and changes freely, this
        // is a contract a partner's site depends on. emitJourneyEvent keeps
        // its own once-guard, so this is safe on every focus.
        emitJourneyEvent(EJourneyEvent.TOOL_ENGAGED);
    }

    // A value present without a single edit was populated for them, which a
    // resumed journey needs to be distinguishable from work they did.
    function resolveFieldStatus(): TrackingFieldStatus {
        const hasValueNow = Boolean(value);
        if (!hasValueNow)
            return hadValueOnFocus.current ? 'cleared' : 'skipped';
        if (editCount.current === 0) return 'prefilled';
        return 'completed';
    }

    function handleKeyPress(
        event: React.KeyboardEvent<HTMLInputElement>
    ): void {
        // Check if Enter key is pressed
        if (event.key === 'Enter' && onArrow) {
            onArrow();
        }
    }

    function handleArrowClick(): void {
        if (onArrow) {
            onArrow();
        }
    }

    function handleBlurWrapper(): void {
        // Call the existing onBlur function from props, if available
        onBlur && onBlur();
        // Call new function
        uniqueIdTransfer({
            eventType: eventType,
            applicationData: { id: id },
        });

        // Blur, not keystroke — keystroke granularity is out of scope. The
        // element id is the field name: it is already stable and unique across
        // the app, so it needs no separate mapping to drift out of step.
        const dwellMs =
            focusedAt.current === null
                ? undefined
                : (typeof performance !== 'undefined'
                      ? performance.now()
                      : Date.now()) - focusedAt.current;

        trackFieldInteraction(
            id,
            resolveFieldStatus(),
            dwellMs,
            editCount.current
        );

        focusedAt.current = null;
    }

    function getDisplayValue(): string {
        if (value === null) return '';
        if (value === undefined) return '';
        return value;
    }

    return (
        <div
            data-testid='input' // 7px either side, so two consecutive fields sit 14px apart. The
            // same value is on RadioGroup and LoanTerm, so the whole screen
            // keeps one rhythm.
            className='py-[7px] w-full relative'
        >
            <div
                className={`w-full flex space-x-1 items-center ${
                    disclaimer ? 'pb-1' : ''
                }`}
            >
                {title && (
                    <FieldLabel
                        title={title}
                        tooltipContent={tooltipContent}
                        required={required}
                        className={`fieldHeading ${disclaimer ? 'pb-0' : ''}`}
                    />
                )}
                {disclaimer && (
                    <InformationCircleIcon
                        height={20}
                        width={20}
                        className='text-quaternary cursor-pointer'
                        onClick={disclaimerClick}
                    />
                )}
            </div>
            <div className='relative'>
                {suffix && suffixLeft !== null && (
                    <span
                        aria-hidden='true'
                        data-testid='inputSuffix'
                        className='absolute top-1/2 -translate-y-1/2 z-50 pointer-events-none leading-none text-textPrimary'
                        style={{ left: suffixLeft }}
                    >
                        {suffix}
                    </span>
                )}
                {prefix && (
                    <span
                        aria-hidden='true'
                        data-testid='inputPrefix'
                        className={`absolute left-3 top-1/2 -translate-y-1/2 z-50 pointer-events-none leading-none ${
                            value ? 'text-textPrimary' : 'text-lightGrey'
                        }`}
                    >
                        {prefix}
                    </span>
                )}
                {unit && (
                    <span
                        aria-hidden='true'
                        data-testid='inputUnit'
                        className='absolute right-3 top-1/2 -translate-y-1/2 z-50 pointer-events-none leading-none text-textBody'
                    >
                        {unit}
                    </span>
                )}
                <input
                    className={trimClassname(
                        `appearance-none border  rounded-[10px] w-full py-2 ${
                            prefix ? 'px-6' : 'px-3'
                        } ${unit ? 'pr-20' : ''} text-textPrimary leading-tight inputButtonFocus placeholderStyle h-11 z-40 ${
                            // A field paints its own surface rather than
                            // inheriting whatever is behind it, so it still
                            // reads as a field on a tinted panel. Skipped
                            // while erroring or pulsing, which set their own.
                            !showErrors && !pulseErrors ? 'bg-white' : ''
                        } ${showErrors ? errorClass : ''} ${
                            pulseErrors && 'bg-yellow-100 animate-pulse'
                        } ${
                            disabled
                                ? 'hover:none cursor-disabled'
                                : 'hover:border-secondary '
                        } ${height === 'large' ? 'h-12' : ''}`
                    )}
                    ref={inputRef}
                    aria-describedby={
                        [
                            tooltipContent ? helpId : null,
                            resolvedMessage ? messageId : null,
                        ]
                            .filter(Boolean)
                            .join(' ') || undefined
                    }
                    id={id}
                    type={type}
                    aria-label={ariaLabel}
                    data-testid='inputField'
                    placeholder={placeholder}
                    value={getDisplayValue()}
                    onChange={handleChange}
                    disabled={disabled}
                    onKeyPress={handleKeyPress}
                    onBlur={handleBlurWrapper}
                    onFocus={handleFocus}
                    inputMode={showNumpad ? 'numeric' : undefined}
                />
            </div>
            {tooltipContent && (
                <span id={helpId} className='sr-only'>
                    {tooltipContent}
                </span>
            )}
            <div
                className={`messageSlot ${
                    resolvedMessage ? 'messageSlotOpen' : ''
                }`}
            >
                <div>
                    <p
                        id={messageId}
                        data-testid='inputMessage'
                        role={tone === 'error' ? 'alert' : undefined}
                        className={`px-3 pt-1 text-xs leading-snug ${
                            tone === 'error'
                                ? 'text-errorText font-semibold'
                                : 'text-textBody'
                        }`}
                    >
                        {resolvedMessage}
                    </p>
                </div>
            </div>
            {onArrow && (
                <div
                    className='absolute inset-y-0 right-0 flex items-center pr-2 cursor-pointer z-50'
                    onClick={handleArrowClick}
                >
                    <ArrowRightCircleIcon className='w-5 h-5 text-primary' />
                </div>
            )}
        </div>
    );
}

export const errorClass = 'error';
